import { useState, useEffect } from 'react';
import { Dialog, DialogPanel, Title, Text, TextInput, Button } from '@tremor/react';
import { api } from '../../lib/api';
import type { ClassifierBundle } from '../../types';

interface DuplicateBundleModalProps {
  isOpen: boolean;
  onClose: () => void;
  source: ClassifierBundle;
  onSuccess: (newKey: string) => void;
}

export function DuplicateBundleModal({ isOpen, onClose, source, onSuccess }: DuplicateBundleModalProps) {
  const [key, setKey] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setKey('');
      setName(`${source.name} (copy)`);
      setDescription(source.description ?? '');
      setError(null);
    }
  }, [isOpen, source]);

  const handleSubmit = async () => {
    if (!key.trim() || !name.trim()) {
      setError('Key and name are required');
      return;
    }

    if (!/^[a-zA-Z][a-zA-Z0-9-]*$/.test(key)) {
      setError('Key must start with a letter and contain only letters, numbers, and hyphens');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const newBundle = await api.duplicateBundle(source.key, {
        key: key.trim(),
        name: name.trim(),
        description: description.trim() || undefined,
      });
      onSuccess(newBundle.key);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to duplicate bundle');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} static={true}>
      <DialogPanel className="bg-gray-900 border border-gray-800 max-w-md">
        <Title className="text-white">Duplicate Classifier Bundle</Title>
        <Text className="text-gray-400 mt-1">
          Creates a new bundle with the same classifiers, label exclusions, and display-name overrides as <span className="font-mono text-gray-300">{source.key}</span>. The new bundle starts as Enabled and is never the default.
        </Text>

        <div className="mt-4 space-y-4">
          <div>
            <label className="text-xs text-gray-400 uppercase tracking-wide">New Key</label>
            <TextInput
              placeholder="e.g., iconect-default-v2"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              className="mt-1"
            />
            <p className="text-xs text-gray-500 mt-1">Stable identifier. Cannot be changed later.</p>
          </div>

          <div>
            <label className="text-xs text-gray-400 uppercase tracking-wide">Name</label>
            <TextInput
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1"
            />
          </div>

          <div>
            <label className="text-xs text-gray-400 uppercase tracking-wide">Description (optional)</label>
            <TextInput
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="mt-1"
            />
          </div>

          {error && (
            <div className="bg-red-950/50 border border-red-900 rounded p-3">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <Button variant="secondary" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={isLoading} disabled={!key.trim() || !name.trim()}>
            Duplicate Bundle
          </Button>
        </div>
      </DialogPanel>
    </Dialog>
  );
}
